import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { formatNumber } from "@/lib/format";
import { useLicenseLabels } from "@/hooks/useLicenseLabels";
import type { SoftwareProduct } from "@/pages/products/hooks/useSoftwareProducts";

interface ProductDetailModalProps {
  product: SoftwareProduct;
  onClose: () => void;
}

export default function ProductDetailModal({ product, onClose }: ProductDetailModalProps) {
  const { t } = useTranslation();
  const { edition } = useLicenseLabels();

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const versions = product.versions ?? [];
  const editions = product.editions ?? [];

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-foreground-950/50 p-0 sm:items-center sm:p-4">
      <button type="button" aria-label={t("products.close")} className="absolute inset-0 cursor-default" onClick={onClose} />
      <div className="lh-fade-up relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-t-lg border border-background-200 bg-background-50 sm:rounded-lg">
        <div className="sticky top-0 flex items-start justify-between gap-4 border-b border-background-200 bg-background-50 px-5 py-4 md:px-6">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-700">
              <i className={`${product.icon} text-xl`} />
            </span>
            <div>
              <h2 className="font-heading text-lg font-semibold text-foreground-950">{product.name}</h2>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                {product.category && (
                  <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-secondary-900">
                    {product.category}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500" />
                  {Number(product.rating).toFixed(1)}
                </span>
                <span>{t("products.downloadsUnit", { count: formatNumber(product.downloads) })}</span>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100"
            aria-label={t("products.closeDetail")}
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-5 py-5 md:px-6">
          <p className="text-sm leading-relaxed text-foreground-700">{product.description}</p>

          <h3 className="mt-6 font-heading text-sm font-semibold text-foreground-950">{t("products.editionsTitle")}</h3>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            {editions.map((item) => (
              <div key={item.key} className="rounded-lg border border-background-200 bg-background-100 p-4">
                <p className="text-sm font-medium text-foreground-950">{edition(item.key)}</p>
                <p className="mt-2 font-heading text-lg font-semibold text-primary-700">
                  {item.price > 0 ? `¥${formatNumber(item.price)}` : t("products.customQuote")}
                </p>
                <p className="mt-1 text-[11px] text-foreground-500">
                  {item.price > 0 ? t("products.perSeat") : t("products.customPlan")}
                </p>
              </div>
            ))}
          </div>

          <h3 className="mt-6 font-heading text-sm font-semibold text-foreground-950">{t("products.versionHistory")}</h3>
          <ol className="mt-3 space-y-3">
            {versions.map((item, index) => (
              <li key={item.version} className="flex gap-3">
                <span className="flex flex-col items-center">
                  <span
                    className={`mt-1 h-2.5 w-2.5 rounded-full ${index === 0 ? "bg-primary-500" : "bg-background-300"}`}
                  />
                  {index < versions.length - 1 && <span className="mt-1 w-px flex-1 bg-background-200" />}
                </span>
                <div className="flex-1 pb-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-foreground-950">{item.version}</span>
                    <span className="text-xs text-foreground-500">{item.date}</span>
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-foreground-600">{item.notes}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t border-background-200 bg-background-50 px-5 py-4 md:px-6">
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            {t("products.close")}
          </button>
          <Link
            to={`/apply?product=${product.slug}`}
            onClick={onClose}
            className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            {t("products.applyThis")}
          </Link>
        </div>
      </div>
    </div>
  );
}